import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FiDownload, FiUserCheck, FiFileText, FiSettings, FiCheckCircle } from 'react-icons/fi';
import Header from '../components/Header';
import Footer from '../components/Footer';
import IconWrapper from '../components/print-steps/IconWrapper';
import { Button } from '../components/ui/button';
import useKYCStore from '../store/kycStore';

interface LocationState {
  shopId: string | null;
  shopAccountId?: string;
}

const ShopRegistrationSuccessPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { setShopAccountId } = useKYCStore();
  const state = location.state as LocationState;
  const hasShopId = state?.shopId !== null && state?.shopId !== undefined;
  
  // Set shop account ID from navigation state if available
  useEffect(() => {
    if (state?.shopAccountId) {
      setShopAccountId(state.shopAccountId);
    }
  }, [state?.shopAccountId, setShopAccountId]);
  
  const handleGoHome = () => {
    navigate('/');
  };
  
  const handleGoToKyc = () => {
    navigate('/kyc-form');
  };
  
  const handleRegisterAnotherShop = () => {
    // Clear shop account ID and navigate back to registration
    setShopAccountId('');
    navigate('/shop-registration');
  };
  
  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />
      
      <main className="flex-grow container mx-auto py-8 px-4">
        <div className="max-w-3xl mx-auto">
          <div className="bg-white rounded-lg shadow-md border border-gray-100 overflow-hidden mb-6">
            <div className="p-6">
              <div className="flex justify-center mb-6">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                  <IconWrapper icon={FiCheckCircle} className="h-8 w-8 text-green-600" />
                </div>
              </div>
              
              <h1 className="text-2xl font-bold text-center text-gray-800 mb-2">
                Registration Submitted Successfully!
              </h1>
              
              {hasShopId ? (
                <div>
                  <p className="text-center text-gray-600 mb-8">
                    Your shop has been registered with ID: <span className="font-semibold">{state.shopId}</span>.
                    Please follow these steps to complete your setup:
                  </p>
                  
                  <div className="space-y-6">
                    <div className="flex">
                      <div className="mr-4 flex-shrink-0">
                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                          <IconWrapper icon={FiUserCheck} className="h-5 w-5 text-blue-600" />
                        </div>
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold mb-2">1. Complete KYC Verification</h3>
                        <p className="text-gray-600 mb-3">
                          Complete the KYC verification process by uploading your documents.
                        </p>
                        <Button 
                          className="bg-blue-600 hover:bg-blue-700 mb-2"
                          onClick={handleGoToKyc}
                        >
                          Start KYC Process
                        </Button>
                      </div>
                    </div>
                    
                    <div className="flex">
                      <div className="mr-4 flex-shrink-0">
                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                          <IconWrapper icon={FiDownload} className="h-5 w-5 text-blue-600" />
                        </div>
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold mb-2">2. Download CTRLP Shopkeeper Dashboard</h3>
                        <p className="text-gray-600 mb-2">
                          Download and install the CTRLP shopkeeper dashboard application from the link below.
                        </p>
                        <Button className="bg-blue-600 hover:bg-blue-700">
                          Download App
                        </Button>
                      </div>
                    </div>
                    
                    <div className="flex">
                      <div className="mr-4 flex-shrink-0">
                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                          <IconWrapper icon={FiFileText} className="h-5 w-5 text-blue-600" />
                        </div>
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold mb-2">3. Prepare Documents</h3>
                        <p className="text-gray-600">
                          Please prepare the following documents for verification:
                        </p>
                        <ul className="list-disc pl-5 mt-2 text-gray-600">
                          <li>Aadhaar Card</li>
                          <li>PAN Card</li>
                          <li>Blank Cheque or Bank Statement or Passbook</li>
                        </ul>
                      </div>
                    </div>
                    
                    <div className="flex">
                      <div className="mr-4 flex-shrink-0">
                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                          <IconWrapper icon={FiSettings} className="h-5 w-5 text-blue-600" />
                        </div>
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold mb-2">4. Complete Setup</h3>
                        <p className="text-gray-600">
                          Complete the setup and wait for the application to detect your printer and its capabilities.
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex">
                      <div className="mr-4 flex-shrink-0">
                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                          <IconWrapper icon={FiCheckCircle} className="h-5 w-5 text-blue-600" />
                        </div>
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold mb-2">5. Go Online</h3>
                        <p className="text-gray-600">
                          Make your shop online and start earning money with CTRLP!
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div>
                  <p className="text-center text-gray-600 mb-8">
                    Thank you for your interest in joining the CTRLP network. Our team is verifying your shop registration request and will get back to you soon.
                  </p>
                  
                  <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
                    <div className="flex">
                      <div className="ml-3">
                        <p className="text-sm text-yellow-700">
                          In the meantime, kindly prepare the following documents for KYC verification:
                        </p>
                        <ul className="list-disc pl-5 mt-2 text-yellow-700 text-sm">
                          <li>Aadhaar Card</li>
                          <li>PAN Card</li>
                          <li>Blank Cheque or Bank Statement or Passbook</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                  
                  <p className="text-center text-gray-600">
                    Our team will contact you at your registered email address with further instructions once your registration has been verified.
                  </p>
                </div>
              )}
              
              <div className="mt-8 text-center">
                <Button 
                  variant="outline"
                  className="border-blue-600 text-blue-600 hover:bg-blue-50 mr-3"
                  onClick={handleGoHome}
                >
                  Back to Home
                </Button>
                {hasShopId && (
                  <Button 
                    variant="outline"
                    className="border-gray-600 text-gray-600 hover:bg-gray-50 mr-3"
                    onClick={handleRegisterAnotherShop}
                  >
                    Register Another Shop
                  </Button>
                )}
                <Button 
                  className="bg-blue-600 hover:bg-blue-700"
                  onClick={handleGoToKyc}
                >
                  Complete KYC Now
                </Button>
              </div>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default ShopRegistrationSuccessPage;